import { FC, useEffect, useState } from "react";
import { BaseModalWindow } from "./BaseModalWindow";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../system/providers/appContextProvider";
import { KizDocDataProvider } from "../../Services/DataProviders/KizDocDataProvider";
import { IKizDocEditDto } from "../../libs/coreapi-dto/dirs/kiz";
import { DateInput, TextInput } from "../controls/inputs/BaseInput";
import styles from './styles/ReviceKizDocWindow.module.scss'
import { IOption, Select } from "../selects/select";
import { IdTableVariant, InvoiceDocType, InvoiceOutDocType, MovementOutDocType } from "../../@types/enumsGlobal";
import { useUserContext } from "../../system/providers/userContextProvider";
import renderGlobalAlert from "../../system/hooks/useGlobalAlert";
import { DateTime } from "luxon";

interface IReviceKizDocWindowProps {
    readonly idDocumentGlobal: string
    readonly cancel: () => void
    readonly ok: () => void
    readonly idTable: IdTableVariant
}

const InvoiceDocTypeOptions: IOption[] = [
    {
        displayName: "416 - обратный акцепт",
        value: InvoiceDocType.InBw,
    } as IOption,
    {
        displayName: "701 - прямой акцепт",
        value: InvoiceDocType.InFw,
    } as IOption,
    {
        displayName: "431 - перемещение между местами деятельности одного участника",
        value: InvoiceDocType.Moving,
    } as IOption,
    {
        displayName: "701 - приемка комиссионного товара по прямому акцепту",
        value: InvoiceDocType.InCm,
    } as IOption,
    {
        displayName: "701 - приемка льготного товара по прямому акцепту",
        value: InvoiceDocType.InLgota,
    } as IOption,
    {
        displayName: "473 - приемка комиссионного товара по обратному акцепту",
        value: InvoiceDocType.InCmBw,
    } as IOption,
    {
        displayName: "391 - возврат выданного незарегистриованному участнику",
        value: InvoiceDocType.DelEnter,
    } as IOption,
    {
        displayName: "0 - схема не должна была отправляться, перевести КИЗы в статус \"в обороте\"",
        value: InvoiceDocType.KssBlocking,
    } as IOption,
];

const InvoiceOutDocTypeOptions: IOption[] = [
    {
        displayName: "415 - отгрузка по прямому акцепту",
        value: InvoiceOutDocType.OutFw,
    } as IOption,
    {
        displayName: "431 - перемещение между местами деятельности одного участника",
        value: InvoiceOutDocType.Moving,
    } as IOption,
    {
        displayName: "441 - отгрузка незарегистриованному участнику",
        value: InvoiceOutDocType.OutUn,
    } as IOption,
    {
        displayName: "701 - подтверждение отгрузки по обратному акцепту",
        value: InvoiceOutDocType.OutBw,
    } as IOption,
    {
        displayName: "472 - отгрузка комиссионного товара по прямому акцепту",
        value: InvoiceOutDocType.OutCmFw,
    } as IOption,
    {
        displayName: "471 - отгрузка льготного товара по прямому акцепту",
        value: InvoiceOutDocType.OutLgotaFw,
    } as IOption,
    {
        displayName: "701 - подтверждение отгрузки комиссионного товара по обратному акцепту",
        value: InvoiceOutDocType.OutCmBw,
    } as IOption,
    {
        displayName: "0 - запрет отправки любой схемы (только для администратора)",
        value: InvoiceOutDocType.KssBlocking,
    } as IOption,
];

const MovementOutDocTypeOptions: IOption[] = [
    {
        displayName: "431 - перемещение между местами деятельности одного участника",
        value: MovementOutDocType.Moving,
    } as IOption,
    {
        displayName: "441 - отгрузка незарегистриованному участнику",
        value: MovementOutDocType.OutUn,
    } as IOption,
    {
        displayName: "470 - перемещение льготного товара между местами деятельности одного участника",
        value: MovementOutDocType.MovingLgota,
    } as IOption,
    {
        displayName: "552 - списание без уничтожения",
        value: MovementOutDocType.DelWithDrawal,
    } as IOption,
    {
        displayName: "0 - запрет отправки любой схемы",
        value: MovementOutDocType.KssBlocking,
    } as IOption,
];

export const ReviceKizDocModalWindow : FC<IReviceKizDocWindowProps> = (props) => {
    const appContext = useAppContext();
    const userContext = useUserContext();
    const kizDocDP = new KizDocDataProvider(appContext.coreApiService)
    const roles = (userContext.userRoles || []).flat().map(role => role.trim());
    const isAdministrator = roles.some(role => role.toLowerCase() === 'administrator');

    const [data, setData] = useState<IKizDocEditDto>()
    const [dataEdit, setDataEdit] = useState<IKizDocEditDto>({
        docNum: '',docType: '', idReceiver: '', idShipper: '', idSubject: '', kizDocGlobalIds: [], shipperInn: ''
    })

    const [docTypeOption, setDocTypeOption] = useState<IOption>({displayName: '', value: ''})
  
    const { t } = useTranslation();
    const baseT = (value: string) => t('modals.reviceKizDocModalWindow.' + value)

    useEffect(() => {
        kizDocDP.GetKizDocByIdDocumentAsync(props.idDocumentGlobal, (response) => {
            setData(response)
            setDataEdit(response)

            const options = getDocTypeOptions(props.idTable)
            const option = options.find(c => c.value == response.docType) ?? {displayName: '', value: ''} as IOption

            setDocTypeOption(option)
        }, () => {
            props.cancel()
        })
    }, [])

    const getDocTypeOptions = (variant: IdTableVariant): IOption[] => {
        switch (variant) {
            case IdTableVariant.Invoice:
                return InvoiceDocTypeOptions;
            case IdTableVariant.InvoiceOut:
                return InvoiceOutDocTypeOptions;
            case IdTableVariant.Movement:
                return MovementOutDocTypeOptions;
            default:
                return [];
        }
    };

    return ( 
        <BaseModalWindow
        header={baseT("header")}
        ok={{
            onClick: () => {
                kizDocDP.revise(dataEdit, () => {props.ok()})
            },
            disabled: false
        }}
        cancel={{
            onClick: () => {
                props.cancel();
            },
        }}
        >
            <div className={styles.basicForm}>
                <div className={styles.formRow}>
                    <span className={styles.formLabel}>{baseT("docNum")}</span>
                    <span className={styles.formValue}>{data?.docNum}</span>
                    <TextInput className={styles.formValue}  disabled={false} value={dataEdit?.docNum} 
                       onChange={(value) => setDataEdit({...dataEdit,docNum: value})}
                    />
                </div>

                <div className={`${styles.formRow} ${styles.headerRow}`}>
                    <span className={styles.formLabel}></span>
                    <span className={styles.formValue}>{baseT("sent")}</span>
                    <span className={styles.formValue}>{baseT("inReference")}</span>
                </div>

                 <div className={styles.formRow}>
                    <span className={styles.formLabel}>{baseT("docDate")}</span>
                    <TextInput className={styles.formValue} value={data?.docDate?.toString()} disabled={true}/>
                    <DateInput 
                        className={styles.formValue}
                         value={dataEdit?.docDate
                            ? DateTime.fromISO(dataEdit?.docDate.toString())
                            : undefined
                        }
                        onBlur={(value) => {
                            if (value) {
                                setDataEdit({...dataEdit,docDate: value})
                            }
                        }}
                    />
                </div>

                <div className={styles.formRow}>
                    <span className={styles.formLabel}>{baseT("idSubject")}</span>
                    <TextInput className={styles.formValue} value={data?.idSubject} disabled={true}/>
                    <TextInput className={styles.formValue}  disabled={false} value={dataEdit?.idSubject} 
                       onChange={(value) => setDataEdit({...dataEdit,idSubject: value})}
                    />
                </div>

                <div className={styles.formRow}>
                    <span className={styles.formLabel}>{baseT("idShipper")}</span>
                    <TextInput className={styles.formValue} value={data?.idShipper} disabled={true}/>
                    <TextInput className={styles.formValue}  disabled={false} value={dataEdit?.idShipper} 
                       onChange={(value) => setDataEdit({...dataEdit, idShipper: value})}
                    />
                </div>

                <div className={styles.formRow}>
                    <span className={styles.formLabel}>{baseT("idReceiver")}</span>
                    <TextInput className={styles.formValue} value={data?.idReceiver} disabled={true}/>
                    <TextInput className={styles.formValue}  disabled={false} value={dataEdit?.idReceiver} 
                       onChange={(value) => setDataEdit({...dataEdit, idReceiver: value})}
                    />
                </div>

                <div className={styles.formRow}>
                    <span className={styles.formLabel}>{baseT("shipperInn")}</span>
                    <TextInput className={styles.formValue} value={data?.shipperInn} disabled={true}/>
                    <TextInput className={styles.formValue}  disabled={false} value={dataEdit?.shipperInn} 
                       onChange={(value) => setDataEdit({...dataEdit, shipperInn: value})}
                    />
                </div>

                <div className={styles.formRow}>
                    <span className={styles.formLabel}>{baseT("docType")}</span>
                     <Select className={`${styles.formValue} ${styles.wide}`}
                        onSelect={(option) => {
                            if (option.value == InvoiceDocType.KssBlocking) {
                                if (isAdministrator) {
                                    setDataEdit({ ...dataEdit, docType: option.value });
                                    setDocTypeOption(option)
                                } else {
                                    renderGlobalAlert({ 
                                        variant: "error", 
                                        statusCode: 500, 
                                        title: baseT("errorMessageTitle"), 
                                        detail: baseT("errorMessageDetail")
                                    });
                                }
                            }
                            else {
                                setDataEdit({ ...dataEdit, docType: option.value });
                                setDocTypeOption(option)
                            }                            
                        }}
                        value={docTypeOption}
                        options={getDocTypeOptions(props.idTable)}
                    />
                </div>

                <div className={styles.warningBlock}>
                    <div className={styles.warningTitle}> {baseT("warningTitile")}</div>
                    <div className={styles.warningText}>
                        {baseT("warningMessage")}
                    </div>
                </div>
            </div>
        </BaseModalWindow>)
}